import React from 'react';
import SafeIcon from '../common/SafeIcon';
import { FiAward, FiTrendingUp, FiTarget, FiZap, FiCheckCircle, FiLock, FiEye, FiCpu } from 'react-icons/fi';
import { motion } from 'framer-motion';

const Performance = ({ stats, xp, ticketsCount, onClaimCertificate, onViewSample }) => {
  const tier1Total = 50;
  const tier2Total = 50;
  
  const isTier1Eligible = stats.tier1Resolved >= tier1Total;
  const isTier2Eligible = stats.tier2Resolved >= tier2Total;

  const tier1Progress = Math.min((stats.tier1Resolved / tier1Total) * 100, 100);
  const tier2Progress = Math.min((stats.tier2Resolved / tier2Total) * 100, 100);

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto space-y-6 md:space-y-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-2">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-slate-800">Analyst Metrics</h1>
          <p className="text-xs md:text-sm text-slate-500">Global performance tracking across all tiers</p>
        </div>
        <div className="text-left sm:text-right">
          <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Total Resolved</div>
          <div className="text-2xl md:text-3xl font-black text-blue-600 leading-none">{stats.resolved}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
        <div className="bg-white p-5 md:p-6 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-2 md:mb-4">
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Total XP</span>
            <SafeIcon icon={FiZap} className="text-blue-500" />
          </div>
          <div className="text-3xl md:text-4xl font-black text-slate-800">{xp}</div>
        </div>
        <div className="bg-white p-5 md:p-6 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-2 md:mb-4">
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Tier 1 Progress</span>
            <SafeIcon icon={FiCheckCircle} className="text-emerald-500" />
          </div>
          <div className="text-3xl md:text-4xl font-black text-slate-800">{tier1Progress.toFixed(0)}%</div>
        </div>
        <div className="bg-white p-5 md:p-6 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-2 md:mb-4">
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Tier 2 Progress</span>
            <SafeIcon icon={FiCpu} className="text-indigo-500" />
          </div>
          <div className="text-3xl md:text-4xl font-black text-slate-800">{tier2Progress.toFixed(0)}%</div>
        </div>
      </div>

      {/* Tier 1 Certification Card */}
      <div className={`rounded-3xl p-6 md:p-8 border-2 transition-all ${isTier1Eligible ? 'bg-gradient-to-br from-blue-600 to-indigo-700 text-white border-blue-400 shadow-xl' : 'bg-white border-slate-200'}`}>
        <div className="flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-8">
          <div className={`w-16 h-16 md:w-24 md:h-24 rounded-2xl md:rounded-full flex items-center justify-center shrink-0 ${isTier1Eligible ? 'bg-white/20' : 'bg-slate-100'}`}>
            <SafeIcon icon={FiAward} className={`text-3xl md:text-4xl ${isTier1Eligible ? 'text-white' : 'text-slate-300'}`} />
          </div>
          <div className="flex-1 w-full">
            <h2 className={`text-lg md:text-xl font-black mb-1 ${isTier1Eligible ? 'text-white' : 'text-slate-800'}`}>Tier 1: Service Associate</h2>
            <p className={`text-xs mb-4 ${isTier1Eligible ? 'text-white/80' : 'text-slate-500'}`}>Proof of competency in foundational support operations.</p>
            <div className="w-full h-2 bg-black/10 rounded-full overflow-hidden mb-4">
              <motion.div initial={{ width: 0 }} animate={{ width: `${tier1Progress}%` }} className={`h-full ${isTier1Eligible ? 'bg-white' : 'bg-blue-500'}`} />
            </div>
            <div className="flex flex-wrap gap-3">
              {isTier1Eligible ? (
                <button onClick={() => onClaimCertificate(1)} className="px-6 py-2 bg-white text-blue-600 rounded-lg font-bold text-xs uppercase tracking-widest shadow-lg">Claim Certificate</button>
              ) : (
                <button onClick={() => onViewSample(1)} className="flex items-center space-x-2 px-4 py-2 bg-slate-100 text-slate-600 rounded-lg font-bold text-xs uppercase tracking-widest">
                  <SafeIcon icon={FiEye} /><span>Preview Sample</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tier 2 Certification Card */}
      <div className={`rounded-3xl p-6 md:p-8 border-2 transition-all ${isTier2Eligible ? 'bg-gradient-to-br from-indigo-700 to-slate-900 text-white border-indigo-500 shadow-xl' : 'bg-white border-slate-200'}`}>
        <div className="flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-8">
          <div className={`w-16 h-16 md:w-24 md:h-24 rounded-2xl md:rounded-full flex items-center justify-center shrink-0 ${isTier2Eligible ? 'bg-indigo-500' : 'bg-slate-100'}`}>
            <SafeIcon icon={isTier2Eligible ? FiAward : FiLock} className={`text-3xl md:text-4xl ${isTier2Eligible ? 'text-white' : 'text-slate-300'}`} />
          </div>
          <div className="flex-1 w-full">
            <h2 className={`text-lg md:text-xl font-black mb-1 ${isTier2Eligible ? 'text-white' : 'text-slate-800'}`}>Tier 2: Senior Infrastructure Analyst</h2>
            <p className={`text-xs mb-4 ${isTier2Eligible ? 'text-white/80' : 'text-slate-500'}`}>Certified expertise in complex systems and technical leadership.</p>
            {!isTier1Eligible ? (
              <div className="inline-flex items-center space-x-2 px-3 py-2 bg-red-50 text-red-600 rounded-lg text-[10px] font-black uppercase tracking-widest border border-red-100">
                <SafeIcon icon={FiLock} /> <span>Complete Tier 1 to Unlock</span>
              </div>
            ) : (
              <>
                <div className="w-full h-2 bg-black/10 rounded-full overflow-hidden mb-4">
                  <motion.div initial={{ width: 0 }} animate={{ width: `${tier2Progress}%` }} className={`h-full ${isTier2Eligible ? 'bg-white' : 'bg-indigo-500'}`} />
                </div>
                <div className="flex flex-wrap gap-3">
                  {isTier2Eligible ? (
                    <button onClick={() => onClaimCertificate(2)} className="px-6 py-2 bg-white text-indigo-700 rounded-lg font-bold text-xs uppercase tracking-widest shadow-lg">Claim Certificate</button>
                  ) : (
                    <button onClick={() => onViewSample(2)} className="flex items-center space-x-2 px-4 py-2 bg-slate-100 text-slate-600 rounded-lg font-bold text-xs uppercase tracking-widest">
                      <SafeIcon icon={FiEye} /><span>Preview Sample</span>
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Performance;